import { type FC, type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DefinitionTooltip, type DefinitionTooltipInfo } from "src/components/common";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import {
  SALES_ATTRIBUTION_ALL_FILTER_VALUE,
  type SalesAttributionCampaignOption,
  type SalesAttributionFilterOptions,
  type SalesAttributionFilterState,
  type SalesAttributionOption,
} from "../utils/SalesAttributionTypes";

const ALL = SALES_ATTRIBUTION_ALL_FILTER_VALUE;

const withAllOption = (
  options: SalesAttributionOption[],
  allLabel: string
): SalesAttributionOption[] => [{ value: ALL, label: allLabel }, ...options];

interface FilterFieldProps {
  label: string;
  value: string;
  options: Array<SalesAttributionOption & { disabled?: boolean }>;
  onChange: (_value: string) => void;
}

const FilterField: FC<FilterFieldProps> = ({ label, value, options, onChange }) => {
  const isSet = value !== "";

  return (
    <div
      className={`sales-attribution__filter-field ${
        isSet ? "sales-attribution__filter-field--set" : ""
      }`}
    >
      <span className="sales-attribution__filter-field-label">{label}</span>
      <div className="sales-attribution__filter-field-control">
        <StyledDropdown
          value={isSet ? value : ALL}
          options={options}
          onChange={next => onChange(next === ALL ? "" : String(next))}
          className={`filter-dropdown ${
            !isSet ? "custom-dropdown--all-selected" : "custom-dropdown--has-value"
          }`}
          popupClassName="filter-dropdown-menu"
          aria-label={label}
          showSearch
          optionFilterProp="label"
        />
        {isSet && <span className="sales-attribution__filter-indicator" aria-hidden="true" />}
      </div>
    </div>
  );
};

interface FilterSectionProps {
  title: string;
  subtitle: string;
  tooltip: DefinitionTooltipInfo;
  className?: string;
  children: ReactNode;
}

const FilterSection: FC<FilterSectionProps> = ({
  title,
  subtitle,
  tooltip,
  className = "",
  children,
}) => (
  <section className={`sales-attribution__filter-section ${className}`.trim()}>
    <header className="sales-attribution__filter-section-header">
      <div className="sales-attribution__filter-section-title-row">
        <h3 className="sales-attribution__filter-section-title">{title}</h3>
        <DefinitionTooltip label={title} info={tooltip} />
      </div>
      <p className="sales-attribution__filter-section-subtitle">{subtitle}</p>
    </header>
    <div className="sales-attribution__filter-section-fields">{children}</div>
  </section>
);

interface SalesAttributionFilterBarProps {
  filters: SalesAttributionFilterState;
  options: SalesAttributionFilterOptions;
  onChange: (_next: SalesAttributionFilterState) => void;
}

export const SalesAttributionFilterBar: FC<SalesAttributionFilterBarProps> = ({
  filters,
  options,
  onChange,
}) => {
  const { t } = useTranslation();

  const allUtmSourcesLabel = t("salesAttribution.ui.filters.allSources", {
    defaultValue: "All UTM Sources",
  });
  const allUtmMediumsLabel = t("salesAttribution.ui.filters.allMediums", {
    defaultValue: "All UTM Mediums",
  });
  const allUtmCampaignsLabel = t("salesAttribution.ui.filters.allUtmCampaigns", {
    defaultValue: "All UTM Campaigns",
  });
  const allAdCampaignsLabel = t("salesAttribution.ui.filters.allCampaigns", {
    defaultValue: "All Ad Campaigns",
  });
  const allPlatformsLabel = t("salesAttribution.ui.filters.allPlatforms", {
    defaultValue: "All Ad Platforms",
  });

  const utmSourceLabel = t("salesAttribution.ui.filters.utmSource", {
    defaultValue: "UTM Source",
  });
  const utmMediumLabel = t("salesAttribution.ui.filters.utmMedium", {
    defaultValue: "UTM Medium",
  });
  const utmCampaignLabel = t("salesAttribution.ui.filters.utmCampaign", {
    defaultValue: "UTM Campaign",
  });
  const platformLabel = t("salesAttribution.ui.filters.platform", {
    defaultValue: "Ad Platform",
  });
  const campaignLabel = t("salesAttribution.ui.filters.campaign", {
    defaultValue: "Ad Campaign",
  });

  const websiteTrafficTitle = t("salesAttribution.ui.filterSections.websiteTraffic.title", {
    defaultValue: "Website Traffic",
  });
  const websiteTrafficSubtitle = t(
    "salesAttribution.ui.filterSections.websiteTraffic.subtitle",
    {
      defaultValue: "UTM tags from Shopify & GA4 · filters store revenue and users",
    }
  );
  const adAccountsTitle = t("salesAttribution.ui.filterSections.adAccounts.title", {
    defaultValue: "Ad Accounts",
  });
  const adAccountsSubtitle = t("salesAttribution.ui.filterSections.adAccounts.subtitle", {
    defaultValue: "Google Ads & Meta Ads · filters ad performance tables",
  });

  const filterNoteBullets = t("salesAttribution.ui.filterNote.bullets", {
    returnObjects: true,
    defaultValue: [
      "UTM Filters (Source, Medium, Campaign): Filter your website tracking data (Shopify and GA4). They won't affect ad platform tables.",
      "Platform & Campaign Filters: Filter ad account reporting (Google Ads and Meta Ads). They won't affect overall store revenue or user totals.",
    ],
  }) as string[];

  const utmFiltersTooltip: DefinitionTooltipInfo = {
    description:
      filterNoteBullets[0] ??
      "UTM Filters (Source, Medium, Campaign): Filter your website tracking data (Shopify and GA4). They won't affect ad platform tables.",
  };
  const platformCampaignTooltip: DefinitionTooltipInfo = {
    description:
      filterNoteBullets[1] ??
      "Platform & Campaign Filters: Filter ad account reporting (Google Ads and Meta Ads). They won't affect overall store revenue or user totals.",
  };

  const utmSourceOptions = useMemo(
    () => withAllOption(options.utmSources, allUtmSourcesLabel),
    [options.utmSources, allUtmSourcesLabel]
  );
  const utmMediumOptions = useMemo(
    () => withAllOption(options.utmMediums, allUtmMediumsLabel),
    [options.utmMediums, allUtmMediumsLabel]
  );
  const utmCampaignOptions = useMemo(
    () => withAllOption(options.utmCampaigns, allUtmCampaignsLabel),
    [options.utmCampaigns, allUtmCampaignsLabel]
  );

  const platformOptions = useMemo(
    () =>
      (options.platforms.length > 0
        ? options.platforms
        : [{ value: ALL, label: allPlatformsLabel }]
      ).map(option => ({
        value: option.value,
        label: option.value === ALL ? allPlatformsLabel : option.label,
        disabled: option.available === false,
      })),
    [options.platforms, allPlatformsLabel]
  );

  const campaignOptions = useMemo(() => {
    const matchesPlatform = (option: SalesAttributionCampaignOption) =>
      !filters.platform ||
      filters.platform === ALL ||
      (option.platforms ?? []).includes(filters.platform);

    return withAllOption(
      options.campaigns.filter(matchesPlatform).map(option => ({
        value: option.value,
        label: option.label,
      })),
      allAdCampaignsLabel
    );
  }, [filters.platform, options.campaigns, allAdCampaignsLabel]);

  const resolvePlatformForCampaign = (campaignValue: string): string => {
    if (!campaignValue) {
      return "";
    }
    const selected = options.campaigns.find(option => option.value === campaignValue);
    const platforms = (selected?.platforms ?? []).filter(
      platform => platform === "meta_ads" || platform === "google_ads",
    );
    // Single-platform campaigns drive Ad Platform; multi/unknown stay on All.
    return platforms.length === 1 ? platforms[0] : "";
  };

  const setField =
    (field: keyof SalesAttributionFilterState) => (value: string) => {
      if (field === "platform") {
        // The chosen campaign may not exist on the new platform.
        onChange({ ...filters, platform: value, campaign: "" });
        return;
      }
      if (field === "campaign") {
        onChange({
          ...filters,
          campaign: value,
          platform: resolvePlatformForCampaign(value),
        });
        return;
      }
      onChange({ ...filters, [field]: value });
    };

  const hasAnyFilterSet =
    filters.utmSource !== "" ||
    filters.utmMedium !== "" ||
    filters.utmCampaign !== "" ||
    filters.platform !== "" ||
    filters.campaign !== "";

  const clearAllFilters = () => {
    onChange({
      utmSource: "",
      utmMedium: "",
      utmCampaign: "",
      platform: "",
      campaign: "",
    });
  };

  return (
    <div className="sales-attribution__filter-sections">
      <div className="sales-attribution__filter-sections-row">
        <FilterSection
          className="sales-attribution__filter-section--traffic"
          title={websiteTrafficTitle}
          subtitle={websiteTrafficSubtitle}
          tooltip={utmFiltersTooltip}
        >
          <FilterField
            label={utmSourceLabel}
            value={filters.utmSource}
            options={utmSourceOptions}
            onChange={setField("utmSource")}
          />
          <FilterField
            label={utmMediumLabel}
            value={filters.utmMedium}
            options={utmMediumOptions}
            onChange={setField("utmMedium")}
          />
          <FilterField
            label={utmCampaignLabel}
            value={filters.utmCampaign}
            options={utmCampaignOptions}
            onChange={setField("utmCampaign")}
          />
        </FilterSection>

        <FilterSection
          className="sales-attribution__filter-section--ads"
          title={adAccountsTitle}
          subtitle={adAccountsSubtitle}
          tooltip={platformCampaignTooltip}
        >
          <FilterField
            label={platformLabel}
            value={filters.platform}
            options={platformOptions}
            onChange={setField("platform")}
          />
          <FilterField
            label={campaignLabel}
            value={filters.campaign}
            options={campaignOptions}
            onChange={setField("campaign")}
          />
        </FilterSection>
      </div>

      {hasAnyFilterSet ? (
        <div className="sales-attribution__filter-sections-actions">
          <button
            type="button"
            className="sales-attribution__filter-bar-clear"
            onClick={clearAllFilters}
          >
            <span className="sales-attribution__filter-bar-clear-text">
              {t("salesAttribution.ui.filters.clear", { defaultValue: "Clear Filters" })}
            </span>
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default SalesAttributionFilterBar;
